/**
 * BMFont definition → measurement metrics.
 *
 * ONE piece of arithmetic, shared by two callers that must not disagree:
 *
 * - `src/build/generate-font-metrics.ts`, which runs it over the bundled
 *   atlases at build time and freezes the answers into
 *   `fontMetrics.generated.ts`.
 * - `hostedFonts.ts`, which runs it over a BMFont JSON fetched at runtime
 *   from Library Admin, for a face that was never bundled.
 *
 * Before this module existed the derivation lived only in the generator, so a
 * runtime-loaded face could only ever have been measured by a second copy of
 * it. Two copies is exactly how a hosted face ends up measuring one em wider
 * than the same face would have measured bundled — the class of bug this
 * whole text stack is built to make impossible (measured face == rendered
 * face, browser == headless). A test pins the runtime path's output against
 * the generated table for a bundled face.
 */
import type { GeneratedFaceMetrics } from './fontMetrics.generated';

/** First printable ASCII char covered by the dense advance table. */
export const BMFONT_FIRST_CHAR = 32;
/** Last printable ASCII char covered by the dense advance table. */
export const BMFONT_LAST_CHAR = 126;

/**
 * The subset of a BMFont JSON that measurement reads. Deliberately narrower
 * than the file: pages, atlas coordinates and the distance-field block are
 * the *renderer's* business (Babylon's `FontAsset` parses the same document
 * for those), and nothing here should grow a dependency on them.
 */
export interface BmFontJson {
    /** `padding` is [up, right, down, left], font units: blank atlas space around each glyph's ink. */
    info: { size: number; padding?: number[] };
    /** `base` is the baseline's depth below the line top, font units. */
    common: { lineHeight: number; base?: number };
    chars: Array<{ id: number; xadvance: number; width: number; height: number; yoffset: number }>;
    kernings?: Array<{ first: number; second: number; amount: number }>;
}

/** charCode of the glyph whose ink top defines a face's cap line. */
const CAP_GLYPH = 72; // 'H'

/**
 * Cap height as a fraction of the ascent (`common.base`), for a face with no
 * 'H' to measure: the middle of the bundled faces' measured range, 0.64
 * (Source Serif 4 Bold) to 0.75 (Caveat Bold), Roboto 0.68, Inter 0.74. It
 * puts every bundled face's cap line within 0.07 em of its measured one
 * (pinned by a test).
 */
export const CAP_HEIGHT_PER_ASCENT = 0.7;

/**
 * Depth of a face's cap line below its line top, in font units: where the ink
 * of a flat capital starts. One number per face, read from the face's own
 * definition and never from the text being drawn, so it is the same for "Once"
 * and "once" and for every line of every paragraph in the face.
 *
 * In order:
 * 1. The 'H' glyph's ink top, `yoffset + padding[up]`. A glyph's `yoffset` is
 *    where its atlas cell starts; the cell carries `padding` of blank
 *    distance-field margin above the ink, so the ink starts that much lower.
 *    (The bundled atlases check out: 'H' ends `padding[down]` below the
 *    baseline, `yoffset + height − padding[down] = base`, for every face
 *    except handwritten Caveat, whose H dips below the baseline.)
 * 2. No 'H' (a face without Latin capitals): `base × (1 − CAP_HEIGHT_PER_ASCENT)`.
 * 3. No usable `base` either: 0, the line top itself.
 *
 * Neither BMFont nor the msdf-atlas-gen JSON declares a cap height, and the
 * atlases are built from the font file without one, so there is no declared
 * value to prefer over 'H'.
 */
export function capTopFontUnits(font: Pick<BmFontJson, 'info' | 'common' | 'chars'>): number {
    const pad = font.info?.padding?.[0];
    const padTop = isFiniteNumber(pad) && pad > 0 ? pad : 0;
    // `?.` throughout: the renderer passes Babylon's parsed copy of the atlas,
    // which `parseBmFont` has not vetted.
    const h = Array.isArray(font.chars) ? font.chars.find((ch) => ch?.id === CAP_GLYPH) : undefined;
    if (h && isFiniteNumber(h.yoffset) && isFiniteNumber(h.height) && h.height > 0) {
        return h.yoffset + padTop;
    }
    const base = font.common?.base;
    if (isFiniteNumber(base) && base > 0) return base * (1 - CAP_HEIGHT_PER_ASCENT);
    return 0;
}

/**
 * Splits one face's glyphs into the dense ASCII advance array and the sparse
 * list for everything above it. `source` names the face in the error.
 */
export function advanceTables(
    source: string,
    chars: ReadonlyArray<{ id: number; xadvance: number }>,
): { advances: number[]; extraAdvances: Array<[number, number]> } {
    // Dense advance array indexed by charCode − BMFONT_FIRST_CHAR; −1 = absent.
    const advances: number[] = new Array<number>(BMFONT_LAST_CHAR - BMFONT_FIRST_CHAR + 1).fill(-1);
    // Every other glyph: [charCode, xadvance] pairs, ascending by charCode.
    const extraAdvances: Array<[number, number]> = [];
    const seen = new Set<number>();
    for (const ch of chars) {
        // One entry per glyph, or the table would silently keep whichever came last.
        if (seen.has(ch.id)) throw new Error(`[font metrics] ${source}: glyph ${ch.id} appears twice`);
        seen.add(ch.id);
        if (ch.id >= BMFONT_FIRST_CHAR && ch.id <= BMFONT_LAST_CHAR) {
            advances[ch.id - BMFONT_FIRST_CHAR] = ch.xadvance;
        } else {
            extraAdvances.push([ch.id, ch.xadvance]);
        }
    }
    extraAdvances.sort((a, b) => a[0] - b[0]);
    return { advances, extraAdvances };
}

/**
 * Derive the measurement table for one face from its BMFont definition.
 *
 * Printable ASCII is a dense array; every other glyph the atlas carries goes
 * into a sparse `[charCode, xadvance, …]` list, so nothing here names a
 * charset. Throws if the definition lists a glyph twice — the measured width
 * would then depend on the order of the JSON.
 */
export function bmfontToMetrics(font: BmFontJson, source = 'BMFont definition'): GeneratedFaceMetrics {
    const { advances, extraAdvances } = advanceTables(source, font.chars);

    // Kerning triples [first, second, amount] for every pair the atlas
    // carries, in the atlas's order. Zero amounts are dropped: Babylon's
    // `_getKerning` answers `map.get(...) || 0`, so a stored 0 is
    // indistinguishable from absent.
    const kernings: number[] = [];
    for (const k of font.kernings ?? []) {
        if (k.amount === 0) continue;
        kernings.push(k.first, k.second, k.amount);
    }

    return {
        emSize: font.info.size,
        commonLineHeight: font.common.lineHeight,
        capTop: capTopFontUnits(font),
        advances,
        extraAdvances: extraAdvances.flat(),
        kernings,
    };
}

/**
 * Parse a BMFont definition and check it carries what measurement needs.
 * Returns `null` (never throws) for anything that isn't a usable definition —
 * a 404 body, an HTML error page served in place of JSON, an XML-format
 * BMFont — so a bad hosted URL degrades to the fallback chain rather than
 * taking the scene down.
 *
 * The numbers measurement reads are checked too: a glyph without a numeric
 * `id` or `xadvance`, or a kerning pair without a number for its amount, would
 * put `undefined` or NaN into the advance tables and NaN into every width laid
 * out with the face. A definition with even one such entry is refused whole,
 * since the atlas it describes cannot be trusted either.
 */
export function parseBmFont(text: string): BmFontJson | null {
    let parsed: unknown;
    try {
        parsed = JSON.parse(text);
    } catch {
        return null;
    }
    if (typeof parsed !== 'object' || parsed === null) return null;
    const candidate = parsed as Partial<BmFontJson>;
    if (!isFiniteNumber(candidate.info?.size) || !(candidate.info.size > 0)) return null;
    if (!isFiniteNumber(candidate.common?.lineHeight)) return null;
    if (!Array.isArray(candidate.chars) || candidate.chars.length === 0) return null;
    if (!candidate.chars.every(hasGlyphAdvance)) return null;
    if (candidate.kernings !== undefined
        && !(Array.isArray(candidate.kernings) && candidate.kernings.every(hasKerningAmount))) return null;
    return candidate as BmFontJson;
}

function isFiniteNumber(value: unknown): value is number {
    return typeof value === 'number' && Number.isFinite(value);
}

function hasGlyphAdvance(ch: unknown): boolean {
    const glyph = ch as Partial<BmFontJson['chars'][number]> | null;
    return Number.isInteger(glyph?.id) && isFiniteNumber(glyph?.xadvance);
}

function hasKerningAmount(pair: unknown): boolean {
    const kerning = pair as Partial<NonNullable<BmFontJson['kernings']>[number]> | null;
    return Number.isInteger(kerning?.first) && Number.isInteger(kerning?.second)
        && isFiniteNumber(kerning?.amount);
}
